import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import Button from '@atlaskit/button/default/button';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { token } from '@atlaskit/tokens';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { filterSuggestions, SuggestionFilterValues } from '../suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from '../useSuggestionsPanelFilters.ts';

import { AiPolicyFooter } from './AiPolicyFooter.tsx';

type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const i18n = defineMessages({
	discardAll: {
		id: 'suggestions-panel.footer.discard-all',
		defaultMessage: 'Discard all',
		description:
			'Button to immediately discard all AI suggestions matching the current panel filter',
	},
});

const styles = cssMap({
	footer: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		position: 'relative',
		zIndex: 1,
		width: '100%',
		paddingBlock: token('space.050'),
		paddingInline: token('space.250'),
		gap: token('space.050'),
	},
	scrim: {
		position: 'absolute',
		insetBlockStart: token('space.negative.200'),
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		zIndex: -1,
		backgroundImage: `linear-gradient(0deg, ${token('elevation.surface.overlay')} 80.78%, transparent 100%)`,
	},
});

/**
 * This component is only used under platform_editor_ai_content_suggested_edits
 * at the moment.
 */
export const SuggestionsFooter: FC = () => {
	const { formatMessage } = useIntl();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const { suggestions } = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		({ aiSuggestionsState }: { aiSuggestionsState?: AISuggestionsSharedState }) => ({
			suggestions: aiSuggestionsState
				? Array.from(aiSuggestionsState.suggestionById.values())
				: undefined,
		}),
	);
	const [{ currentFilter }] = useSuggestionsPanelFilters();
	const { userId } = useSessionData();
	const visibleSuggestions = suggestions
		? filterSuggestions(suggestions, currentFilter, userId)
		: undefined;

	const handleDiscardAll = () => {
		if (!visibleSuggestions?.length) {
			return;
		}

		const actions = fullPageEditorPresetAPI?.aiSuggestions?.actions;
		if (currentFilter === SuggestionFilterValues.ALL) {
			actions?.discardAllSuggestions();
		} else {
			actions?.discardAllSuggestions(visibleSuggestions.map(({ id }) => id));
		}
	};

	return (
		<footer css={styles.footer} data-testid="suggestions-panel-footer">
			<div aria-hidden css={styles.scrim} />
			<Button
				appearance="default"
				shouldFitContainer
				isDisabled={!visibleSuggestions?.length}
				onClick={handleDiscardAll}
			>
				{formatMessage(i18n.discardAll)}
			</Button>
			<AiPolicyFooter />
		</footer>
	);
};
